import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { useTasks } from "../Hooks/useTasks";
import { useParams } from "react-router-dom";
import getInitials from "../../../Utils/getInitials";
import { formatDateTime } from "../../../Utils";
import InfiniteScroll from "react-infinite-scroll-component";
import { fetchComments } from "../../../Services/siteServices";
import { MESSAGES } from "../Constants";

const LIMIT = 10;

const TaskComments = () => {
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const { addComment } = useTasks({ fetch: false });
  const [comments, setComments] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const { instanceId: taskId } = useParams();
  const isMobile =
    Capacitor.getPlatform() !== "web" ||
    /Mobi|Android/i.test(navigator.userAgent);

  const loadMoreComments = async () => {
    setLoading(true);
    const res = await fetchComments(taskId, {
      limit: LIMIT,
      offset: offset,
    });

    if (res?.data?.results?.length > 0) {
      setComments((prev) => [...prev, ...res.data.results]);
      setOffset((prev) => prev + LIMIT);
    } else {
      setHasMore(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMoreComments();
  }, []);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const comment = await addComment(taskId, newComment);
    if (comment) {
      setComments((prev) => [comment, ...prev]);
      setNewComment("");
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden px-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-primary border-b-2 border-gray-300 pb-1">
          Comments
        </h3>
      </div>

      <div
        id="task-comments-scroll"
        className="flex-1 overflow-y-auto custom-scrollbar pr-1"
      >
        {comments.length > 0 ? (
          <InfiniteScroll
            dataLength={comments.length}
            next={loadMoreComments}
            hasMore={hasMore}
            loader={
              loading && (
                <div className="flex justify-center py-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500"></div>
                </div>
              )
            }
            scrollableTarget="task-comments-scroll"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <AnimatePresence>
              {comments.map((c, idx) => (
                <motion.div
                  key={idx}
                  className="border border-gray-200 rounded-md bg-gray-50 p-2 mb-2 shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  <div className="flex items-start justify-center gap-2">
                    <div className="shrink-0">
                      {c?.created_by?.profile_photo ? (
                        <img
                          className="w-6 h-6 rounded-full object-cover"
                          src={c.created_by.profile_photo}
                          alt={c.created_by.first_name}
                        />
                      ) : (
                        <div className="flex justify-center items-center text-sm font-semibold text-white rounded-full w-9 h-9 bg-gray-500">
                          {getInitials(
                            `${c?.created_by?.first_name} ${c?.created_by?.last_name}`
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-800 font-semibold text-sm">
                          {c.created_by.first_name} {c.created_by.last_name}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {formatDateTime(c.created)}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm mt-1 whitespace-pre-line">
                        {c.comment}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </InfiniteScroll>
        ) : loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="p-4">
            <p className="text-gray-400 text-sm text-center py-6">
              {MESSAGES.NO_COMMENTS}
            </p>
          </div>
        )}
      </div>

      <div className="my-2 flex gap-2 items-center pt-2">
        <input
          type="text"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
        />
        <button
          className="bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-blue-600 transition flex items-center gap-1"
          onClick={handleAddComment}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default TaskComments;
